import {
  type CrossResourceAvailabilityRuleId,
  dependentRequirementRank,
} from "./crossResourceAvailability";
import type { GrantLike, RoleOption } from "./roles";

export type SpaceGrant = GrantLike & {
  userGroupId: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
};

export type SupplierPrincipal =
  | { userGroupId: string }
  | { userId: string }
  | { serviceAccountId: string };

export type SpacePrincipalRoles = {
  rolesByGroupId: Record<string, RoleOption[]>;
  rolesByUserId: Record<string, RoleOption[]>;
  rolesByServiceAccountId: Record<string, RoleOption[]>;
};

function principalOf(
  g: SpaceGrant
): { key: string; principal: SupplierPrincipal } | null {
  if (g.userGroupId)
    return { key: g.userGroupId, principal: { userGroupId: g.userGroupId } };
  if (g.userId) return { key: g.userId, principal: { userId: g.userId } };
  if (g.serviceAccountId)
    return {
      key: g.serviceAccountId,
      principal: { serviceAccountId: g.serviceAccountId },
    };
  return null;
}

// For each principal in the space, every grant at its strongest requirement tier
// (lowest rank) — all of them, since same-rank grants can still carry different
// permissions (e.g. space:manage vs space:read). Grants conferring no relevant
// access are dropped; first-seen principal order is preserved.
export function strongestSpaceGrants(
  spaceGrants: readonly SpaceGrant[],
  ruleId: CrossResourceAvailabilityRuleId
): { principal: SupplierPrincipal; grants: SpaceGrant[] }[] {
  const strongest = new Map<
    string,
    { principal: SupplierPrincipal; grants: SpaceGrant[]; rank: number }
  >();
  for (const g of spaceGrants) {
    const resolved = principalOf(g);
    if (!resolved) continue;
    const rank = dependentRequirementRank(ruleId, g);
    if (rank === Number.POSITIVE_INFINITY) continue;
    const prev = strongest.get(resolved.key);
    if (!prev || rank < prev.rank)
      strongest.set(resolved.key, {
        principal: resolved.principal,
        grants: [g],
        rank,
      });
    else if (rank === prev.rank) prev.grants.push(g);
  }
  return [...strongest.values()].map(({ principal, grants }) => ({
    principal,
    grants,
  }));
}

// Maps supplier grants (each carrying a builtin role) to the RoleOptions each
// principal should be pre-staged with when a space is added to that supplier.
// Roles absent from roleOptions (e.g. when the viewer can't assign them) are
// dropped.
export function roleDefaultsForSpace(
  supplierGrants: readonly {
    userGroupId?: string;
    userId?: string;
    serviceAccountId?: string;
    builtinRoleId?: string;
  }[],
  roleOptions: readonly RoleOption[]
): SpacePrincipalRoles {
  const optionByBuiltin = new Map<string, RoleOption>(
    roleOptions.flatMap((r) =>
      r.builtinRoleId ? [[r.builtinRoleId, r] as const] : []
    )
  );
  const rolesByGroupId: Record<string, RoleOption[]> = {};
  const rolesByUserId: Record<string, RoleOption[]> = {};
  const rolesByServiceAccountId: Record<string, RoleOption[]> = {};
  for (const g of supplierGrants) {
    const opt = g.builtinRoleId
      ? optionByBuiltin.get(g.builtinRoleId)
      : undefined;
    if (!opt) continue;
    const id = g.userGroupId ?? g.userId ?? g.serviceAccountId;
    if (!id) continue;
    const target = g.userGroupId
      ? rolesByGroupId
      : g.userId
        ? rolesByUserId
        : rolesByServiceAccountId;
    target[id] = [...(target[id] ?? []), opt];
  }
  return { rolesByGroupId, rolesByUserId, rolesByServiceAccountId };
}
